import { FunctionComponent, useState } from 'react';
import { H1 } from '@app/shop/components/typography';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Flex, Box } from '@rebass/emotion';
import {
  AvailableShippingMethodsQuery,
  AvailableShippingMethodsQuery_magento2_customerCart_shippingAddresses_availableShippingMethods as AvailableShippingMethods,
} from '@app/shop/pages/Checkout/Delivery/__generated__/AvailableShippingMethodsQuery';
import {
  SetShippingMethod,
  SetShippingMethodVariables,
} from '@app/shop/pages/Checkout/Delivery/__generated__/SetShippingMethod';
import messages from '@app/shop/pages/Checkout/Delivery/Delivery.messages';
import { FormattedMessage } from 'react-intl';
import { Button } from '@app/shop/components/Button';
import Card from '@app/components/Card';
import { isNotNullish } from '@app/helpers/predicate';
import useCartId from '@app/shop/hooks/useCartId';
import {
  Notice,
  Error,
} from '@app/shop/pages/Checkout/Delivery/Delivery.styles';
import CheckoutBackLink from '@app/shop/components/CheckoutBackLink';
import FormattedMoney from '@app/shop/components/FormattedMoney';
import { stripHTML } from '@app/shop/helpers';
import useGTMTracking from '@app/shop/hooks/useGTMTracking';
import useCart from '@app/shop/hooks/useCart';

const Delivery: FunctionComponent = () => {
  const history = useHistory();
  const { language = 'en' } = useParams<{ language?: string }>();

  const { cartId } = useCartId();
  const { items, prices, storeConfig } = useCart();
  const [selected, setSelected] = useState('');
  const [error, setError] = useState(false);

  useGTMTracking({ step: 'delivery', items, prices, storeConfig });

  const { data, loading } = useQuery<AvailableShippingMethodsQuery>(
    GET_SHIPPING_METHODS,
    {
      fetchPolicy: 'network-only',
      ssr: false,
      onCompleted: (data) => {
        const shippingMethods =
          data?.magento2?.customerCart?.shippingAddresses[0]
            ?.availableShippingMethods;
        const carrierCode = getCheapestShippingMethodIndex(shippingMethods);
        setSelected(carrierCode);
      },
    }
  );

  const [setShippingMethod] = useMutation<
    SetShippingMethod,
    SetShippingMethodVariables
  >(SET_SHIPPING_METHOD, {
    onCompleted: () => {
      history.push(
        generatePath('/:language/shop/checkout/review/', { language })
      );
    },
    onError: () => {
      setError(true);
    },
  });

  if (loading) {
    return null;
  }

  const notices = data?.magento2?.cmsBlocks?.items?.filter(isNotNullish);
  const notice = isNotNullish(notices) ? stripHTML(notices[0].content) : '';

  const shippingMethods =
    data?.magento2?.customerCart?.shippingAddresses[0]
      ?.availableShippingMethods;

  return (
    <>
      <H1>
        <FormattedMessage {...messages.title} />
      </H1>
      <Box mt={4}>
        <Notice role="complementary">{notice}</Notice>
      </Box>
      <Flex my={4} justifyContent="space-between" flexWrap="wrap">
        {' '}
        {error && (
          <Box mb={1} width={1}>
            <Error>
              <FormattedMessage {...messages.error} />
            </Error>
          </Box>
        )}
        {shippingMethods?.map((method) =>
          method ? (
            <Box key={method.carrierCode} width={0.49}>
              <Card
                data-testid={`shipping-method-${method.carrierCode}`}
                highlight={selected === method.carrierCode}
                disabled={selected !== method.carrierCode}
                onClick={() => setSelected(method.carrierCode)}
                css={{ cursor: 'pointer', marginBottom: '0.75rem' }}
              >
                <h2>
                  {method.carrierTitle} (<FormattedMoney {...method.amount} />)
                </h2>
              </Card>
            </Box>
          ) : null
        )}
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" pt={3}>
        <CheckoutBackLink to="/:language/shop/checkout/address">
          <FormattedMessage {...messages.back} />
        </CheckoutBackLink>
        <Button
          type="primary"
          data-testid="delivery-continue-button"
          onClick={async () => {
            if (isNotNullish(shippingMethods)) {
              const selectedMethod = shippingMethods.find(
                (method) => method?.carrierCode == selected
              );
              if (
                isNotNullish(selectedMethod) &&
                isNotNullish(selectedMethod.methodCode)
              ) {
                await setShippingMethod({
                  variables: {
                    cartId: cartId!,
                    carrierCode: selectedMethod.carrierCode,
                    methodCode: selectedMethod.methodCode,
                  },
                });
              }
            }
          }}
        >
          <FormattedMessage {...messages.continue} />
        </Button>
      </Flex>
    </>
  );
};

export default Delivery;

const getCheapestShippingMethodIndex = (
  shippingMethods?: (AvailableShippingMethods | null)[] | null
): string => {
  let lowest = Number.POSITIVE_INFINITY;
  let carrierCode = '';
  shippingMethods?.forEach((shippingMethod) => {
    if (isNotNullish(shippingMethod)) {
      const value = shippingMethod?.amount.value;
      if (typeof value === 'number' && Number.isFinite(value)) {
        if (value < lowest) {
          lowest = value;
          carrierCode = shippingMethod.carrierCode;
        }
      }
    }
  });
  return carrierCode;
};

export const GET_SHIPPING_METHODS = gql`
  query AvailableShippingMethodsQuery {
    magento2 {
      customerCart {
        id
        shippingAddresses {
          availableShippingMethods {
            amount {
              currency
              value
            }
            carrierTitle
            carrierCode
            methodTitle
            methodCode
          }
        }
      }
      cmsBlocks(identifiers: ["shipping-notice"]) {
        items {
          identifier
          content
        }
      }
    }
  }
`;

export const SET_SHIPPING_METHOD = gql`
  mutation SetShippingMethod(
    $cartId: String!
    $carrierCode: String!
    $methodCode: String!
  ) {
    magento2 {
      setShippingMethodsOnCart(
        input: {
          cart_id: $cartId
          shipping_methods: [
            { carrier_code: $carrierCode, method_code: $methodCode }
          ]
        }
      ) {
        cart {
          id
        }
      }
    }
  }
`;
